import { FC } from 'react';

import { DateTime } from 'luxon';

import { getConvertedTsuboValue } from '@utils_iqfu/getConvertedTsuboValue';
import { getFloatNumberRoundValue } from '@utils_iqfu/getFloatNumberRoundValue';
import { getJapaneseYearText } from '@utils_iqfu/getJapaneseYearText';

import SellingContractActionArea from '@components_iqfu/iqfu_pro/selling_contract_action_area/SellingContractActionArea';

import {
  IqfuProResultPropertyType,
  IqfuProBuildingStructures,
  SellingContract,
} from '@recoil_iqfu/atoms/iqfu_pro/searchQuery';

import { LandCurrentStatus, MonthOutputFormat, ResidenceCurrentStatus, yaerOutputFormat } from '@constants_iqfu';

import styles from './SellingContractCard.module.scss';

export const HouseSellingContract: FC<{ sellingContract: SellingContract }> = ({ sellingContract }) => {
  //成約年月と築年月を引いて　成約時の築年数を計算
  const getBuiltYear = (_contactDate: string, _builtDate: string) => {
    const contactDate = DateTime.fromISO(_contactDate);
    const builtDate = DateTime.fromISO(_builtDate);
    // 経過期間を計算する
    const duration = contactDate.diff(builtDate, ['years', 'months', 'days']);
    return duration.years;
  };

  return (
    <li className={`${styles.container}`}>
      <div className={styles.head_row}>
        <div className={styles.head_col}>
          <p className={styles.property_info}>{IqfuProResultPropertyType[sellingContract.property_type]}</p>
          <p className={styles.head_price}>
            <span className={styles.head_price_number}>{sellingContract.price.toLocaleString()}</span>
            <span className={styles.head_price_unit}>万円</span>
          </p>
        </div>
        <p className={`${styles.bold} ${styles.head_sub_col}`}>
          {DateTime.fromISO(sellingContract.contract_date).toFormat(MonthOutputFormat)}
          成約
        </p>
        <p className={`${styles.head_sub_col} ${styles.address}`}>{sellingContract.address_summary}</p>
      </div>
      <div className={styles.house_price_detail_row}>
        {sellingContract.land_price && sellingContract.land_area ? (
          <>
            <p className={`${styles.house_price_detail_row_head} ${styles.mobile_mb_4}`}>内訳</p>

            <p>
              <span className={styles.data_head}>土地価格:</span>
              <span className={styles.breakdown_price}>{sellingContract.land_price?.toLocaleString()}万円</span>
            </p>

            <p className={styles.mobile_mb_4}>
              <span className={styles.data_head}>単価:</span>
              <span className={`${styles.text_blue} ${styles.bold}`}>
                {getFloatNumberRoundValue(sellingContract.land_price / sellingContract.land_area, 1)}万円/㎡ (
                {getFloatNumberRoundValue(
                  sellingContract.land_price / getConvertedTsuboValue(sellingContract.land_area, 2),
                  1,
                )}
                万円/坪)
              </span>
            </p>
          </>
        ) : (
          <></>
        )}
        {sellingContract.building_price && sellingContract.building_area ? (
          <>
            <p>
              <span className={styles.data_head}>建物価格:</span>
              <span className={styles.breakdown_price}>
                {sellingContract.building_price?.toLocaleString()}万円
                {sellingContract.built_date &&
                  `(築${getBuiltYear(sellingContract.contract_date, sellingContract.built_date)}年)`}
              </span>
            </p>
            <p>
              <span className={styles.data_head}>単価:</span>
              <span className={`${styles.text_blue} ${styles.bold}`}>
                {getFloatNumberRoundValue(sellingContract.building_price / sellingContract.building_area, 1)}
                万円/㎡ (
                {getFloatNumberRoundValue(
                  sellingContract.building_price / getConvertedTsuboValue(sellingContract.building_area, 2),
                  1,
                )}
                万円/坪)
              </span>
            </p>
          </>
        ) : (
          <></>
        )}
      </div>
      <div className={styles.detail_row_stacks}>
        <div className={styles.detail_column}>
          {sellingContract.land_area ? (
            <p>
              <span className={styles.data_head}>土地面積:</span>
              {sellingContract.land_area}㎡ ({getConvertedTsuboValue(sellingContract.land_area, 2)}
              坪)
            </p>
          ) : (
            <></>
          )}
          {sellingContract.building_area ? (
            <p>
              <span className={styles.data_head}>建物面積:</span>
              {sellingContract.building_area}㎡ ({getConvertedTsuboValue(sellingContract.building_area, 2)}
              坪)
            </p>
          ) : (
            <></>
          )}
        </div>

        {sellingContract.building_structure && (
          <div className={styles.detail_column}>
            <p>
              <span className={styles.data_head}>構造:</span>
              {IqfuProBuildingStructures[sellingContract.building_structure]}
            </p>
            {sellingContract.floor && (
              <p>
                <span className={styles.data_head}>階数:</span>
                {sellingContract.floor}階
              </p>
            )}
            {sellingContract.built_date && (
              <p>
                <span className={styles.data_head}>建築年:</span>
                {DateTime.fromISO(sellingContract.built_date).toFormat(yaerOutputFormat)}(
                {getJapaneseYearText(sellingContract.built_date)})
              </p>
            )}

            <p>
              <span className={styles.data_head}>状態：</span>
              {ResidenceCurrentStatus[sellingContract.current_status as keyof typeof ResidenceCurrentStatus]}
            </p>
            <p>
              <span className={styles.data_head}>リフォーム:</span>
              {sellingContract.has_reformed ? '有り' : '無し'}
            </p>
          </div>
        )}
      </div>
      <div className={styles.action_area_container}>
        <SellingContractActionArea sellingContract={sellingContract} />
      </div>
    </li>
  );
};

export const LandSellingContract: FC<{ sellingContract: SellingContract; inProMap?: boolean }> = ({
  sellingContract,
}) => {
  return (
    <li className={`${styles.container}`}>
      <div className={styles.head_row}>
        <div className={styles.head_col}>
          <p className={styles.property_info}>{IqfuProResultPropertyType[sellingContract.property_type]}</p>
          <p className={styles.head_price}>
            <span className={styles.head_price_number}>{sellingContract.price.toLocaleString()}</span>
            <span className={styles.head_price_unit}>万円</span>
          </p>
        </div>
        <div className={`${styles.head_sub_col}`}>
          <p className={styles.bold}>
            {DateTime.fromISO(sellingContract.contract_date).toFormat(MonthOutputFormat)}
            成約
          </p>
          {sellingContract.price && sellingContract.land_area ? (
            <p>
              <span className={`${styles.text_blue} ${styles.bold}`}>
                {getFloatNumberRoundValue(sellingContract.price / sellingContract.land_area, 1)}万円/㎡ (
                {getFloatNumberRoundValue(
                  sellingContract.price / getConvertedTsuboValue(sellingContract.land_area, 2),
                  1,
                )}
                万円/坪)
              </span>
            </p>
          ) : (
            <></>
          )}
        </div>
        <p className={`${styles.head_sub_col} ${styles.address}`}>{sellingContract.address_summary}</p>
      </div>
      <div className={styles.detail_column}>
        {sellingContract.land_area && (
          <p>
            <span className={styles.data_head}>土地面積:</span>
            {sellingContract.land_area}㎡ ({getConvertedTsuboValue(sellingContract.land_area, 2)}
            坪)
          </p>
        )}

        <p>
          <span className={styles.data_head}>状態：</span>
          {LandCurrentStatus[sellingContract.current_status as keyof typeof LandCurrentStatus]}
        </p>
      </div>
      <div className={styles.action_area_container}>
        <SellingContractActionArea sellingContract={sellingContract} />
      </div>
    </li>
  );
};

export const MansionSellingContract: FC<{ sellingContract: SellingContract; inProMap?: boolean }> = ({
  sellingContract,
}) => {
  return (
    <li className={`${styles.container}`}>
      <div className={styles.head_row}>
        <div className={styles.head_col}>
          <p className={styles.property_info}>{sellingContract.mansion_name}</p>
          <p className={styles.head_price}>
            <span className={styles.head_price_number}>{sellingContract.price.toLocaleString()}</span>
            <span className={styles.head_price_unit}>万円</span>
          </p>
        </div>
        <div className={`${styles.head_sub_col}`}>
          <p className={`${styles.bold}`}>
            {DateTime.fromISO(sellingContract.contract_date).toFormat(MonthOutputFormat)}
            成約
          </p>
          {sellingContract.occupied_area && sellingContract.price ? (
            <p>
              <span className={`${styles.text_blue} ${styles.bold}`}>
                {getFloatNumberRoundValue(sellingContract.price / sellingContract.occupied_area, 1)}万円/㎡
              </span>
            </p>
          ) : (
            <></>
          )}
        </div>
        <p className={`${styles.head_sub_col} ${styles.address}`}>{sellingContract.mansion_full_address}</p>
      </div>

      <div className={styles.detail_column}>
        {sellingContract.occupied_area && (
          <p>
            <span className={styles.data_head}>専有面積:</span>
            {sellingContract.occupied_area}㎡ ({getConvertedTsuboValue(sellingContract.occupied_area, 2)}
            坪)
          </p>
        )}
        {sellingContract.floor && (
          <p>
            <span className={styles.data_head}>階数:</span>
            {sellingContract.floor}階
          </p>
        )}
        {sellingContract.floor_plan && (
          <p>
            <span className={styles.data_head}>間取り:</span>
            {sellingContract.floor_plan === 'unknown' ? '不明' : sellingContract.floor_plan}
          </p>
        )}
        <p>
          <span className={styles.data_head}>状態：</span>
          {ResidenceCurrentStatus[sellingContract.current_status as keyof typeof ResidenceCurrentStatus]}
        </p>
        <p>
          <span className={styles.data_head}>リフォーム:</span>
          {sellingContract.has_reformed ? '有り' : '無し'}
        </p>
      </div>
      <div className={styles.action_area_container}>
        <SellingContractActionArea sellingContract={sellingContract} />
      </div>
    </li>
  );
};
